<script setup lang="ts">
import type { Article } from "~/types/article"
import { keys } from "~/utilities/injection"

const props = defineProps<{
  data: Article
}>()

const route = useRoute<"article">()

const articleData = computed(() => props.data)

provide(keys.currentArticle, articleData)

const { data: mathTasksSolutions } = useMathTaskSolutions(articleData)

const articleEl = useTemplateRef<{ SIDENOTE_OFFSET: number }>("article")
const article = computed(() => {
  try {
    const templateName = props.data.template
    return defineAsyncComponent(
      () => import(`../Article/${templateName}.vue`),
    )
  } catch {
    throw new Error(
      `Article template ${props.data.template} not found`,
    )
  }
})

function closeNote() {
  return navigateTo({ hash: "", query: route.query }, {
    replace: true,
  })
}

const activeNote = computed(() => {
  const sidenotes = [
    ...(props.data.relations ?? []),
    ...(mathTasksSolutions.value?.sidenotes ?? []),
  ]

  return sidenotes.find(({ locationId }) => `#${locationId}` === route.hash)
})
</script>

<template>
  <div
    v-if="!data"
    class="flex h-full w-full items-center justify-center bg-platform-paper p-2 font-national2compressed text-4xl text-black"
  >
    <h1 class="h-fit w-fit">
      Beklager, vi har problemer med å vise artikkelen du etterspurte.
    </h1>
  </div>
  <div
    v-else
    :id="`article-page-${data.id}`"
    class="grid h-full min-h-0 w-full overflow-hidden bg-platform-paper"
    :style="{
      '--sidebar-width': '475px',
      '--offset': articleEl?.SIDENOTE_OFFSET ?? 0,
    }"
  >
    <div
      class="col-start-1 row-start-1 h-full min-h-full w-full transform text-base transition-transform md:text-xl lg:text-2xl"
      :class="{
        'sidenoteactive overflow-hidden': !!activeNote,
      }"
      @click.self="route.hash ? closeNote() : null"
    >
      <component
        :is="article"
        ref="article"
        :data="data"
        class="h-full w-full"
        :inert="!!route.hash"
      />
    </div>
    <USidenoteContainer :active-note="activeNote" @close="closeNote" />
  </div>
</template>

<style scoped>
.sidenoteactive {
  @screen md {
    --tw-translate-x: calc(var(--sidebar-width) * var(--offset) * -1);
  }
}
</style>
